import * as React from 'react'
import {Box, Typography} from '@mui/material'
import {kpiCardSx, kpiValueSx, kpiLabelSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {SUCCESS, PURPLE, WARNING, PRIMARY_BLUE, ERROR, SUBTLE, withOpacity} from '../../../../styles/foundation/colors'
import {int} from '../format'

/**
 * ORDER: the tab row beneath this strip reads Initiate | Under review |
 * Approved | Rejected, so the figures read in that same order, and each subset
 * sits immediately right of the figure it is a subset of. A merchant scanning
 * down from a card to the tabs should not have to re-find their place —
 * requests/RqStats.js's approved-first order would put Approved above the
 * Under review tab.
 *
 * COLOURS mostly mirror the `toneColor`-backed palette the chips in this
 * section use (STATUS_TONE in data/reference.js) and RqStats.js:18-23, so the
 * strip keeps doubling as the status colour legend — four of the five must
 * not drift from that palette. 'asked' is the deliberate exception: the tab
 * row this strip sits directly above is PRIMARY_BLUE, and that chip's tone
 * resolves to a second, slightly different blue — adjacent near-duplicate
 * blues read as a mismatch rather than a legend, so this card takes the
 * row's own PRIMARY_BLUE instead.
 *
 * `tab` marks a card as a destination. Only the three that map 1:1 onto a tab
 * carry one: 'prelim' and 'asked' are SUBSETS of approved and review, so a
 * click would land on a tab whose count openly disagrees with the card the
 * merchant just pressed. Those two stay text.
 */
export const LABEL = 'Request statuses'

const REQUEST_CARDS = [
  {id: 'review', label: 'Under review with Prozo', color: WARNING, tab: 'review', hint: 'opens the Under review tab'},
  {id: 'asked', label: 'Waiting on data from you', color: PRIMARY_BLUE},
  {id: 'approved', label: 'Approved', color: SUCCESS, tab: 'approved', hint: 'opens the Approved weight freezes tab'},
  {id: 'prelim', label: 'Still preliminary, under 2 cycles', color: PURPLE},
  {id: 'rejected', label: 'Rejected', color: ERROR, tab: 'rejected', hint: 'opens the Rejected tab'},
]

/**
 * The five request-status cards with their figures filled in from
 * `requestCounts` (requests/logic.js), which reads the WHOLE collection: the
 * strip answers "where does this merchant stand", and it sits above a search
 * card further down the page whose keystrokes must not be able to renumber it.
 * That is the reason requestCounts and filterRequests are two functions — see
 * requests/logic.js's own header.
 *
 * A builder rather than a `counts` prop on the strip itself, because the strip
 * now also heads Package master, whose figures are per-leaf and come from
 * three different derivations. One preset per caller keeps the component free
 * of any one caller's vocabulary.
 */
export function requestCards(counts) {
  return REQUEST_CARDS.map((c) => ({...c, value: counts[c.id]}))
}

/**
 * Weight Freeze (v2) — the stat-card strip that heads a sub-module, above its
 * tab row and therefore visible on every view of it.
 *
 * Used twice: the Freeze-weight dashboard's five request-status figures (see
 * `requestCards` above, and FreezeDashboard.js), and Package master's per-leaf
 * figures, which change with the active tab because they describe what the
 * merchant is looking at rather than the module as a whole.
 *
 * A card is `{id, value, label, color}`, optionally `note` (a plain sentence
 * under the label, for a figure that needs a denominator or a caveat) and
 * `tab` + `hint` (makes it a real button that calls `onSelect(tab)`).
 * `value` is a raw number — formatting is this component's job so two callers
 * cannot group thousands two ways.
 *
 * Metrics mirror disputes-v2/KpiStrip.js, which is the treatment the merchant
 * pointed at: the shared helpers below carry the larger tier three other
 * modules use, so the compact overrides (p 1.25, 18px value, 2xs medium label)
 * are re-stated here exactly as KpiStrip.js:96-113 re-states them. No icon —
 * the reference cards have none, and the coloured left accent already carries
 * the legend that RqStats.js's icon carries there.
 */
export default function RqKpiStrip({cards, label = LABEL, onSelect}) {
  const n = cards.length

  return (
    // A CSS grid, not a flex-wrap row, so all the cards are exactly the same
    // height whatever their labels wrap to (grid items stretch by default —
    // deliberately not overridden), and a wide strip collapses in steps instead
    // of wrapping raggedly. Template and gap from KpiStrip.js:76, stepped by
    // card count so a two-card strip does not stretch two cards across five
    // columns' worth of page.
    //
    // The named group is what StepRail's `nav` landmark is for the row below:
    // without it a screen reader meets a handful of bare numbers with nothing
    // saying how many there are or what they are collectively about.
    <Box
      role='group'
      aria-label={label}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${Math.min(n, 2)}, 1fr)`,
          md: `repeat(${Math.min(n, 3)}, 1fr)`,
          lg: `repeat(${n}, 1fr)`,
        },
        gap: 1,
      }}
    >
      {cards.map((c) => {
        const value = int(c.value)
        const clickable = !!c.tab && typeof onSelect === 'function'

        return (
          <Box
            key={c.id}
            // A real <button> when it acts like one, so the destinations are
            // keyboard-reachable and announce themselves. `type='button'`
            // matters: a bare button inside a form submits it.
            component={clickable ? 'button' : 'div'}
            type={clickable ? 'button' : undefined}
            onClick={clickable ? () => onSelect(c.tab) : undefined}
            // "Approved" alone says neither what the number counts nor what
            // pressing it does.
            aria-label={clickable ? `${value} ${c.label} — ${c.hint}` : undefined}
            sx={{
              ...kpiCardSx(c.color),
              // KpiStrip.js:96-101 — the compact tier, and the native button
              // reset that stops the UA centring the text or imposing its font.
              p: 1.25,
              height: 'auto',
              width: '100%',
              textAlign: 'left',
              font: 'inherit',
              cursor: clickable ? 'pointer' : 'default',
              ...(clickable
                ? {
                  '&:hover': {bgcolor: withOpacity(c.color, 0.06)},
                  // Focus has to be VISIBLE: these sit on white and the UA ring
                  // is easy to lose against the card's own coloured accent.
                  '&:focus-visible': {outline: `2px solid ${c.color}`, outlineOffset: '2px'},
                }
                // kpiCardSx lifts every card on hover. On a card that is not a
                // destination that advertises an interaction which does not
                // exist — worse than no hover at all, since it makes the inert
                // cards look MORE pressable than the buttons beside them.
                : {'&:hover': {}}),
            }}
          >
            <Typography sx={{...kpiValueSx(c.color), fontSize: '18px', lineHeight: 1.25}}>{value}</Typography>
            <Typography sx={{...kpiLabelSx, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium, mb: 0}}>
              {c.label}
            </Typography>
            {!!c.note && (
              <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.4}}>
                {c.note}
              </Typography>
            )}
          </Box>
        )
      })}
    </Box>
  )
}
